import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { generateOwnerReceipt } from '../services/pdfGenerator';
import { getOwnerSettlements, getOwnerProfile } from '../supabaseService';
import {
  buildOwnerReceiptPayload,
  isOwnerProfileIncomplete,
  mapOwnerProfileForReceipt,
} from '../utils/ownerReceiptPayload';

const readSessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
  } catch {
    return {};
  }
};

export default function OwnerReceiptPreview() {
  const [searchParams] = useSearchParams();
  const settlementId = searchParams.get('settlementId') || '';
  const sessionUser = useMemo(() => readSessionUser(), []);

  const [settlement, setSettlement] = useState(null);
  const [apiProfile, setApiProfile] = useState(null);
  const [loading, setLoading] = useState(Boolean(settlementId));
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!settlementId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const items = await getOwnerSettlements(undefined, { throwOnError: true });
        const match = (items || []).find((row) => String(row.id) === String(settlementId));
        if (!cancelled) {
          if (!match) {
            setSettlement(null);
            setError('Settlement not found for this owner.');
          } else {
            setSettlement(match);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setSettlement(null);
          setError(err?.message || 'Unable to load settlement.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [settlementId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getOwnerProfile();
      if (!cancelled && result.profile) setApiProfile(result.profile);
    })();
    return () => { cancelled = true; };
  }, []);

  const receiptPayload = useMemo(() => {
    if (!settlement) return null;
    return buildOwnerReceiptPayload(settlement, sessionUser, apiProfile || {});
  }, [settlement, sessionUser, apiProfile]);

  const handleDownload = async () => {
    if (!receiptPayload) return;
    let profile = apiProfile;
    if (!profile) {
      const result = await getOwnerProfile();
      profile = result.profile;
      if (profile) setApiProfile(profile);
    }
    if (isOwnerProfileIncomplete(mapOwnerProfileForReceipt(profile || {}, sessionUser))) {
      setError('Profile incomplete — update your account name before downloading receipt.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      await generateOwnerReceipt(buildOwnerReceiptPayload(settlement, sessionUser, profile || {}));
    } catch (err) {
      setError(err?.message || 'Could not generate receipt');
    } finally {
      setGenerating(false);
    }
  };

  if (process.env.NODE_ENV === 'production') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#050d1a', color: '#8896a8' }}>
        <p>Preview not available in production.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #050d1a, #0a1628)', color: '#e8e0d0', fontFamily: 'Arial, sans-serif', padding: '32px 20px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 16, padding: 24, background: 'rgba(15,32,64,0.6)' }}>
        <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: 13, margin: '0 0 6px' }}>DEV PREVIEW</p>
        <h1 style={{ color: '#fff', fontSize: 22, margin: '0 0 8px' }}>Premium Owner Receipt</h1>
        <p style={{ color: '#8896a8', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>
          Live settlement receipt from <code>booking_settlements</code>. Pass <code>?settlementId=&lt;uuid&gt;</code> in the URL.
        </p>

        {loading && <p style={{ color: '#8896a8', fontSize: 12 }}>Loading settlement…</p>}

        {!loading && !settlementId && (
          <p style={{ color: '#FF9800', fontSize: 12, margin: '0 0 16px' }}>
            No settlementId in URL. Open Owner Dashboard → Reports & Pay → row PDF, or add <code>?settlementId=…</code>.
          </p>
        )}

        {!loading && settlement && receiptPayload && (
          <div style={{ marginBottom: 16, fontSize: 12, color: '#8896a8', lineHeight: 1.55 }}>
            <p style={{ margin: '0 0 4px' }}><strong style={{ color: '#e8e0d0' }}>Booking:</strong> {receiptPayload.bookingRef}</p>
            <p style={{ margin: '0 0 4px' }}><strong style={{ color: '#e8e0d0' }}>Net:</strong> ₹{Number(receiptPayload.netAmount || 0).toLocaleString('en-IN')}</p>
            <p style={{ margin: 0 }}><strong style={{ color: '#e8e0d0' }}>Owner:</strong> {receiptPayload.ownerName || '—'}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleDownload}
          disabled={generating || loading || !receiptPayload}
          style={{
            width: '100%', padding: '14px', border: 'none', borderRadius: 10, cursor: generating || !receiptPayload ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', fontWeight: 700, fontSize: 14,
            opacity: generating || !receiptPayload ? 0.6 : 1,
          }}
        >
          {generating ? 'Generating PDF…' : '⬇ Download Premium Receipt PDF'}
        </button>
        {error && <p style={{ color: '#e94560', fontSize: 12, margin: '12px 0 0' }}>{error}</p>}
        <p style={{ color: '#8896a8', fontSize: 11, margin: '16px 0 0' }}>
          Or open <Link to="/owner" style={{ color: '#c9a84c' }}>Owner Dashboard</Link> → Reports & Pay → Receipt PDF
        </p>
      </div>
    </div>
  );
}
